import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { geofenceAPI } from '../services/api';
import Swal from 'sweetalert2';
import { getErrorMessage } from '../lib/utils';
import { MapPin, Plus, Edit, Trash2, Target } from 'lucide-react';

const GeofenceManagement = () => {
  const [geofences, setGeofences] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState(null);
  const [newGeofence, setNewGeofence] = useState({
    name: '',
    latitude: '',
    longitude: '',
    radius_meters: 100
  });

  useEffect(() => {
    fetchGeofences();
  }, []);

  const fetchGeofences = async () => {
    try {
      const response = await geofenceAPI.list();
      setGeofences(response.data);
    } catch (error) {
      console.error('Error fetching geofences:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await geofenceAPI.create(newGeofence);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Geofence created successfully',
        timer: 1500,
        showConfirmButton: false
      });
      setShowAddDialog(false);
      setNewGeofence({
        name: '',
        latitude: '',
        longitude: '',
        radius_meters: 100
      });
      fetchGeofences();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error, 'Failed to create geofence')
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'hsl(var(--destructive))',
      cancelButtonColor: 'hsl(var(--muted-foreground))',
      confirmButtonText: 'Yes, delete it!'
    });
    
    if (result.isConfirmed) {
      try {
        await geofenceAPI.delete(id);
        Swal.fire('Deleted!', 'Geofence has been deleted.', 'success');
        fetchGeofences();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete geofence', 'error');
      }
    }
  };

  const calculateCoverage = (radius) => {
    const area = (3.14159 * (radius / 1000) ** 2);
    return area.toFixed(2);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNewGeofence({
            ...newGeofence,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          });
          Swal.fire({
            icon: 'success',
            title: 'Location detected',
            timer: 1200,
            showConfirmButton: false
          });
        },
        (error) => {
          Swal.fire('Error', 'Unable to get location', 'error');
        }
      );
    } else {
      Swal.fire('Error', 'Geolocation not supported', 'error');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="geofence-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-lg">
            <MapPin className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Geofence Management</h2>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-geofence-button" className="bg-primary hover:bg-primary/90 shadow-sm hover:shadow active:scale-95 transition-all">
              <Plus className="h-4 w-4 mr-2" />
              Add Geofence
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md animate-scale-in">
            <DialogHeader>
              <DialogTitle>Create New Geofence</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Location Name</Label>
                <Input
                  placeholder="Office HQ, Branch 1, etc."
                  value={newGeofence.name}
                  onChange={(e) => setNewGeofence({...newGeofence, name: e.target.value})}
                  required
                  className="focus-visible:ring-primary"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="0.000001"
                    placeholder="37.7749"
                    value={newGeofence.latitude}
                    onChange={(e) => setNewGeofence({...newGeofence, latitude: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="0.000001"
                    placeholder="-122.4194"
                    value={newGeofence.longitude}
                    onChange={(e) => setNewGeofence({...newGeofence, longitude: e.target.value})}
                    required
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={getCurrentLocation}
                className="w-full hover:bg-muted active:scale-95 transition-all"
              >
                <Target className="h-4 w-4 mr-2" />
                Use Current Location
              </Button>

              <div>
                <Label>Radius (meters)</Label>
                <Input
                  type="number"
                  min="10"
                  max="5000"
                  value={newGeofence.radius_meters}
                  onChange={(e) => setNewGeofence({...newGeofence, radius_meters: e.target.value})}
                  required
                  className="focus-visible:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Coverage: ~{calculateCoverage(newGeofence.radius_meters)} km²
                </p>
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 shadow-sm hover:shadow active:scale-95 transition-all">Create Geofence</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Geofences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">{geofences.length}</div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Policies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[hsl(var(--chart-2))]">
              {geofences.filter(g => g.is_active).length}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Coverage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {geofences.reduce((sum, g) => sum + parseFloat(calculateCoverage(g.radius_meters)), 0).toFixed(1)} km²
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="bg-muted/50 border-b">
          <CardTitle className="text-lg font-semibold text-foreground">Active Geofences</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {geofences.map((geofence, index) => (
              <div
                key={geofence.id}
                className="p-4 border border-border rounded-lg hover:shadow-md transition-all duration-200 hover:bg-muted/30 bg-card group animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-primary/10 text-primary rounded-md">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <h3 className="font-semibold text-foreground">{geofence.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        geofence.is_active 
                          ? 'bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] border border-[hsl(var(--chart-2))]/20' 
                          : 'bg-muted text-muted-foreground border border-border'
                      }`}>
                        {geofence.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-muted-foreground space-y-1 ml-9">
                      <p className="flex items-center gap-2">
                        <span className="w-20 text-xs font-medium uppercase tracking-wider text-muted-foreground">Coordinates:</span> 
                        <span className="font-mono bg-muted px-1 rounded">{geofence.latitude}, {geofence.longitude}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="w-20 text-xs font-medium uppercase tracking-wider text-muted-foreground">Radius:</span>
                        <span>{geofence.radius_meters}m <span className="text-muted-foreground">(~{calculateCoverage(geofence.radius_meters)} km²)</span></span>
                      </p>
                      <a
                        href={`https://www.google.com/maps?q=${geofence.latitude},${geofence.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/90 hover:underline inline-flex items-center gap-1 mt-1 text-xs font-medium"
                      >
                        View on Google Maps →
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(geofence.id)}
                      className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive rounded-full transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {geofences.length === 0 && (
              <div className="text-center py-12 bg-muted/10 rounded-lg border border-dashed border-border">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-foreground font-medium">No geofences configured</p>
                <p className="text-sm text-muted-foreground mt-1">Add your first location policy to get started</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="text-sm">How Geofencing Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-2 text-muted-foreground">
            <li>✓ Employees must be within geofence radius to mark attendance</li>
            <li>✓ Multiple geofences can be created for different office locations</li>
            <li>✓ Location is validated in real-time during check-in/check-out</li>
            <li>✓ Violations are logged as fraud alerts</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeofenceManagement;
